<template>
    <HeroLayout
        id="sign-in"
        :enable-login-offset="true"
        display-separator
        :overlay-style-overrides="overlayStyleOverrides"
    >
        <template v-if="firstName" #title> Welcome Back, {{ firstName }}</template>
        <template v-else #title>Welcome Back</template>

        <template #bottom-subtitle>Sign in to continue</template>

        <template #overlay-body>
            <div class="vehicle-header-container">
                <div v-if="vehicleId" class="vehicle-header-bg py-2 mb-3">
                    <vehicle-header :vehicle-id="vehicleId" />
                </div>
            </div>

            <social-logins />

            <validation-observer ref="form" v-slot="{ passes }">
                <b-form @submit.stop.prevent="passes(onSubmit)">
                    <validation-provider v-slot="validationContext" name="email" rules="required|email">
                        <b-form-group label="Email" label-for="email">
                            <b-form-input
                                id="email"
                                v-model="form.email"
                                type="email"
                                :state="getValidationState(validationContext)"
                                placeholder="Enter Email"
                                autocomplete="email"
                                aria-describedby="email-live-feedback"
                                :required="true"
                            />
                            <b-form-invalid-feedback id="email-live-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider v-slot="validationContext" name="password" :rules="{ required: true, min: 6 }">
                        <b-form-group
                            label="Password"
                            label-for="password"
                            class="password-input-container position-relative mb-1"
                        >
                            <b-form-input
                                id="password"
                                v-model="form.password"
                                :type="passwordInputType"
                                :state="getValidationState(validationContext)"
                                autocomplete="current-password"
                                placeholder="Enter Password"
                                aria-describedby="password-live-feedback"
                                :required="true"
                            />
                            <primary-link
                                class="show-password-icon position-absolute d-flex"
                                :class="{
                                    'show-password-icon-offset': getValidationState(validationContext) !== null,
                                }"
                                :aria-label="passwordIconLabel"
                                @click.native="handleShowHidePassword"
                            >
                                <i class="material-icons red-eye-icon-size" aria-hidden="true"> remove_red_eye </i>
                            </primary-link>
                            <b-form-invalid-feedback id="password-live-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <div v-if="showLoginError" class="login-error d-block invalid-feedback">
                        {{ loginError }}
                    </div>
                    <b-row>
                        <b-col>
                            <div class="float-right mb-3 font-italic forgot">
                                <primary-link @click.native="forgotPassword(form.email)">
                                    forgot password?
                                </primary-link>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <div class="d-flex justify-content-center flex-wrap">
                                <span class="pr-1">Don't have an account?</span>
                                <primary-link to="/account/register" @click.native="trackClick('createAccount')">
                                    Create an Account
                                </primary-link>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3 mt-md-4">
                        <b-col>
                            <div class="tos font-italic d-flex justify-content-center">
                                <span>
                                    By clicking the button below you agree to the
                                    <primary-link
                                        to="/tos"
                                        target="_blank"
                                        aria-label="Terms of Service (New Window)"
                                        @click.native="trackClick('terms')"
                                    >
                                        Terms
                                    </primary-link>
                                    and
                                    <primary-link
                                        to="/privacy"
                                        target="_blank"
                                        aria-label="Privacy Policy (New Window)"
                                        @click.native="trackClick('privacy')"
                                    >
                                        Privacy Policy
                                    </primary-link></span
                                >
                            </div>
                        </b-col>
                    </b-row>
                    <primary-btn
                        type="submit"
                        class="d-block sign-in-btn mt-2 mt-md-3"
                        :loading="loading"
                        :disabled="disableAllBtns"
                        @click="trackClick('submit')"
                    >
                        Sign In
                    </primary-btn>
                </b-form>
            </validation-observer>
        </template>
    </HeroLayout>
</template>

<script>
import { call, get } from "vuex-pathify";
import HeroLayout from "Components/HeroLayout";
import VehicleHeader from "Modules/Profile/components/VehicleHeader";
import SocialLogins from "../components/SocialLogins";
import PrimaryBtn from "Components/Buttons/PrimaryBtn";
import PrimaryLink from "Components/Links/primaryLink";
import lodashGet from "lodash/get";
import lodashIsEmpty from "lodash/isEmpty";
import CampaignProgramEnum, { isNissanUpgradeCampaignProgram } from "@/libs/CampaignProgramEnum";

export default {
    name: "Login",
    components: {
        PrimaryLink,
        PrimaryBtn,
        SocialLogins,
        VehicleHeader,
        HeroLayout,
    },

    data() {
        return {
            form: {
                email: null,
                password: null,
            },
            loginError: null,
            loading: false,
            loadingNissanSSOBtn: false,
            passwordValueShowing: false,
            overlayStyleOverrides: {
                maxWidth: "500px",
            },
        };
    },
    computed: {
        firstName: get("context/firstName"),
        environments: get("context/environments"),
        ssos: get("campaign/ssos"),
        programId: get("campaign/programId"),
        ssoEnabled() {
            return !lodashIsEmpty(this.environments) && this.environments[0] !== "prod" && !lodashIsEmpty(this.ssos);
        },
        passwordIconLabel() {
            return this.passwordValueShowing ? "hide password" : "show password";
        },
        disableAllBtns() {
            let disabled = false;

            if (this.loading || this.loadingNissanSSOBtn) {
                disabled = true;
            }

            return disabled;
        },
        passwordInputType() {
            return this.passwordValueShowing ? "text" : "password";
        },
        showLoginError() {
            return this.loginError && lodashGet(this.$refs.form, "flags.pristine", false);
        },
        vehicleId() {
            return lodashGet(this.$route, "query.vehicleId");
        },
        isBMWProgram() {
            return CampaignProgramEnum.BMW.indexOf(this.programId) !== -1;
        },
        isNissanUpgradeProgram() {
            return isNissanUpgradeCampaignProgram(this.programId);
        },
    },
    methods: {
        login: call("context/login"),
        getValidationState({ errors, valid = null }) {
            if (this.showLoginError) {
                return false;
            }

            return errors[0] ? false : valid ? true : null;
        },
        onSubmit() {
            const srpFiltersObject = sessionStorage.getItem("SRP_FILTERS");
            this.loginError = null;
            this.loading = true;

            this.login({
                email: this.form.email,
                password: this.form.password,
            })
                .then((success) => {
                    if (!success) {
                        this.loading = false;
                        this.loginError = "The email address or password are not correct";
                        this.$refs.form.reset();
                    } else {
                        // IF BMW Upgrade Program & filters object in session storage -> Redirect to SRP
                        if ((this.isBMWProgram || this.isNissanUpgradeProgram) && srpFiltersObject) {
                            document.location = "/listings";
                        } else {
                            document.location = "/login-success";
                        }
                    }
                })
                .catch((err) => {
                    console.error(err);
                    this.loading = false;
                    this.$refs.form.reset();
                    this.loginError = "Sorry, there was an unexpected error, please try again";
                });
        },
        handleShowHidePassword() {
            this.passwordValueShowing = !this.passwordValueShowing;
        },
        forgotPassword(emailParam) {
            this.trackClick("forgotPassword");
            this.$router.push({
                name: "account-forgot-password",
                params: { email: emailParam },
            });
        },
        trackClick(name) {
            if (name === "submit") {
                this.$carsaverEventTracker("signInHomeBottomBtn");
            }
            if (name === "forgotPassword") {
                this.$carsaverEventTracker("signInForgotPassword");
            }
            if (name === "createAccount") {
                this.$carsaverEventTracker("signInCreateAccount");
            }
            if (name === "terms") {
                this.$carsaverEventTracker("signInTerms");
            }
            if (name === "privacy") {
                this.$carsaverEventTracker("signInPrivacy");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.show-password-icon {
    color: $gray-300;
    z-index: 100;
    right: 10px;
    top: 27px;

    &:hover {
        color: $gray-300;
    }
}

.vehicle-header-bg {
    background-color: $gray-200;
}

/* applied if the is-valid or is-invalid class is on the field*/
.show-password-icon-offset {
    right: 27px !important;
}

.red-eye-icon-size {
    font-size: px2rem(18);
}

.login-error {
    text-align: center;
    padding-bottom: 10px;
}

.sign-in-btn {
    width: 100%;
}

.forgot {
    font-size: px2rem(12);
    line-height: px2rem(16);
}

.tos {
    font-size: px2rem(10);
    line-height: px2rem(12);
    color: $gray-600;
}

@include media-breakpoint-up(md) {
    .form-group {
    }
    .forgot {
    }
}
</style>
<style lang="scss">
#sign-in {
    label {
        font-size: px2rem(12);
        line-height: 14px;
        margin-bottom: 4px;
        font-weight: normal;
        color: $gray-800;
    }

    .title-text {
        margin: 0 auto;
        text-overflow: ellipsis;
        max-width: 80%;
        white-space: nowrap;
        overflow: hidden;
    }
}
</style>
